/* eslint-disable no-param-reassign */
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from '@musinsa/packages-http';
import { ERROR_401_EXPIRED_ACCESS_TOKEN } from './constants';
import { createCookieStore } from './cookieUtils';
import { refreshAccessTokenClient } from './refreshToken';
import { executeRefresh } from './tokenManager';
import type { InterceptorOptions } from './types';

/**
 * Axios 인스턴스에 인증 인터셉터를 설정합니다 (클라이언트 전용).
 *
 * NOTE: 서버 사이드에서는 사용하지 않습니다.
 * 서버 사이드 인증 처리는 app 레이어에서 직접 처리합니다.
 *
 * - Request Interceptor:
 *   - x-device-id 없으면 refresh 시도 안 함 (config 그대로 반환)
 *   - _user_segment 또는 _funuc 없으면 비로그인으로 간주 → onRefreshFail 후 reject
 *   - access_token 없으면 refresh API 호출 시도 (deviceId 있을 때만)
 * - Response Interceptor (401 에러):
 *   - x-device-id 없으면 refresh 시도 안 함, onRefreshFail 호출 후 reject
 *   - errorCode === EXPIRED_ACCESS_TOKEN일 때만 refresh 시도 (deviceId 있을 때만)
 *
 * @param instance - Axios 인스턴스
 * @param options - 인터셉터 옵션 (onRefreshFail 등)
 *
 * @example
 * 이 함수는 httpClient.ts의 getHttpInstance() 내부에서만 호출됩니다.
 * API 레이어에서는 privateHttpClient 또는 getHttpInstance() + createHttpClient()를 사용하세요.
 *
 * ```typescript
 * // 일반적인 인증 API: privateHttpClient 사용
 * import { privateHttpClient } from '../httpClient';
 *
 * // 커스텀 리프레시 실패 핸들러가 필요한 경우에만 getHttpInstance 옵션으로 전달
 * const instance = getHttpInstance({ onRefreshFail: () => customRedirect() });
 * const client = createHttpClient(instance);
 * ```
 */
export const setupAuthInterceptors = (
  instance: AxiosInstance,
  options: InterceptorOptions,
): void => {
  // 클라이언트 전용: document.cookie 사용
  const cookieStore = createCookieStore({});

  // Request Interceptor: Authorization 헤더 추가 및 토큰 없을 시 refresh
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // 0. 구버전 앱에서 x-device-id가 없으면 레거시 요청으로 처리. refresh 불가능
      const deviceId = cookieStore.get('x-device-id');
      if (!deviceId) {
        return config;
      }
      // 1. 비로그인 사용자. (_user_segment 또는 _funuc 중 하나라도 있어야 인증 통과)
      const userSegment = cookieStore.get('_user_segment');
      const legacyLoginFlag = cookieStore.get(
        `_funuc${process.env.NEXT_PUBLIC_SERVICE_ENV === 'production' ? '' : '_qa'}`,
      );
      if (!userSegment && !legacyLoginFlag) {
        options.onRefreshFail?.(); // 기본: 로그아웃 페이지로 이동 (해당 페이지에서 로그인 리다이렉트)
        return Promise.reject(new Error('Not logged in'));
      }

      // 2. access_token과 있는 경우 (refresh_token 검사 불필요)
      const accessToken = cookieStore.get('access_token');
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
        config.headers['x-device-id'] = deviceId;
        return config;
      }

      // 3. access_token이 없는 경우
      try {
        await executeRefresh(() => refreshAccessTokenClient());

        // 새 토큰으로 API 요청 진행
        const newToken = cookieStore.get('access_token');

        config.headers.Authorization = `Bearer ${newToken}`;
      } catch (refreshError) {
        // Refresh 실패 → onRefreshFail 호출 (기본: 로그아웃 페이지로 이동 후 로그인 리다이렉트)
        options.onRefreshFail?.();
        return Promise.reject(refreshError);
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  // Response Interceptor: 401 에러 시 토큰 리프레시 및 재요청
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<{ meta?: { errorCode?: string } }>) => {
      // 기존 요청에 대한 config가 없으면 에러 그대로 반환. refresh 이후 처리가 불가능.
      if (!error.config) {
        return Promise.reject(error);
      }

      const originalRequest = {
        _retry: false,
        ...error.config,
        headers: {
          ...error.config?.headers,
        },
      };

      if (originalRequest._retry) {
        options.onRefreshFail?.();
        return Promise.reject(error);
      }

      // 0. 구버전 앱에서 x-device-id가 없으면 refresh 불가 → 로그아웃 처리
      const deviceId = cookieStore.get('x-device-id');
      if (!deviceId) {
        options.onRefreshFail?.();
        return Promise.reject(error);
      }

      const { status, data } = error.response ?? {};
      if (status === 401) {
        if (data?.meta?.errorCode === ERROR_401_EXPIRED_ACCESS_TOKEN) {
          // 재시도 플래그 설정
          originalRequest._retry = true;

          // 2. refresh 처리
          // 클라이언트: refresh_token은 HttpOnly이므로 읽을 수 없음
          //            바로 refresh API 호출 시도 (API 결과로 판단)
          try {
            await executeRefresh(() => refreshAccessTokenClient());

            // 새 토큰으로 재요청
            const newToken = cookieStore.get('access_token');
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            const result = await instance.request(originalRequest);
            return result;
          } catch (refreshError) {
            // Refresh 실패 → onRefreshFail 호출 (기본: 로그아웃 페이지로 이동 후 로그인 리다이렉트)
            options.onRefreshFail?.();
            return Promise.reject(refreshError);
          }
        } else {
          // 401 + 토큰 만료가 아닌 경우
          options.onRefreshFail?.();
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    },
  );
};
